/**
 * The team grid's two dialogs.
 *
 * Every card shares one bio dialog and one contact dialog rather than carrying
 * its own. What changes between cards is the content, not the chassis: the bio
 * is cloned out of the clicked card's <template>, and the contact form's hidden
 * field is set to the clicked person's name.
 *
 * That hidden field is a NAME, never an address. The Gravity Form's notification
 * routes on it against a table of addresses held in the form's own settings, so
 * editing the value in devtools can only pick a different colleague — it cannot
 * point the mail anywhere new. A hidden field carrying a real mailbox would make
 * the form an open relay.
 *
 * Focus handling follows the speaker enquiry modal (speaker-inquire.js): the
 * same focusable set, the same trap, the same restore. It is duplicated rather
 * than imported because the two live in different packages — the enquiry modal
 * is the theme's and this is the plugin's — and a shared module across that line
 * would make the plugin depend on a theme file it cannot guarantee is there.
 */

const FOCUSABLE = [
	'a[href]',
	'button:not([disabled])',
	'input:not([disabled]):not([type="hidden"])',
	'select:not([disabled])',
	'textarea:not([disabled])',
	'[tabindex]:not([tabindex="-1"])',
].join( ',' );

/**
 * Give one dialog its open/close behaviour.
 *
 * @param {HTMLElement} modal The dialog.
 * @return {{open: Function, close: Function, el: HTMLElement}} Controls.
 */
function createModal( modal ) {
	let lastFocused = null;

	const isOpen = () => ! modal.hasAttribute( 'hidden' );

	const focusable = () =>
		Array.from( modal.querySelectorAll( FOCUSABLE ) ).filter(
			( el ) => null !== el.offsetParent
		);

	function open() {
		lastFocused = document.activeElement;
		modal.removeAttribute( 'hidden' );
		document.body.classList.add( 'dn-modal-open' );

		// The close button rather than the first field: a dialog that lands the
		// caret in a text input has already started filling a form the visitor
		// may only have wanted to look at.
		const close = modal.querySelector( '.dn-modal-close' );
		const first = close || focusable()[ 0 ];

		if ( first ) {
			first.focus();
		}
	}

	function close() {
		if ( ! isOpen() ) {
			return;
		}

		modal.setAttribute( 'hidden', '' );
		document.body.classList.remove( 'dn-modal-open' );

		if ( lastFocused && 'function' === typeof lastFocused.focus ) {
			lastFocused.focus();
		}
	}

	modal.addEventListener( 'click', ( event ) => {
		if ( event.target.closest( '[data-dn-team-close]' ) ) {
			event.preventDefault();
			close();
		}
	} );

	document.addEventListener( 'keydown', ( event ) => {
		if ( ! isOpen() ) {
			return;
		}

		if ( 'Escape' === event.key ) {
			close();
			return;
		}

		if ( 'Tab' !== event.key ) {
			return;
		}

		const items = focusable();

		if ( ! items.length ) {
			return;
		}

		const first = items[ 0 ];
		const last = items[ items.length - 1 ];

		if ( event.shiftKey && document.activeElement === first ) {
			event.preventDefault();
			last.focus();
		} else if ( ! event.shiftKey && document.activeElement === last ) {
			event.preventDefault();
			first.focus();
		}
	} );

	return { open, close, el: modal };
}

/**
 * Bring a sent contact form back, so the next colleague gets a form rather than
 * the last one's thank-you.
 *
 * Gravity Forms' AJAX handler answers a successful submission by replacing the
 * whole #gform_wrapper_{id} with its confirmation message. In a shared dialog
 * that meant: send Alice a message, close, click Bob — and Bob's dialog showed
 * "Thanks, your message has been sent", with no form and no hidden staff field
 * left to route a second message (its <input> went with the wrapper).
 *
 * The fix keeps a clean copy of the wrapper from the first time the dialog
 * opens, and swaps it back in for the confirmation on the next open. That is
 * safe to do because GF keeps everything that drives a submission OUTSIDE the
 * wrapper — the hidden iframe the form posts into and the `load` handler bound
 * to it (form_display.php) — and both survive the swap. Then GF's own
 * post-render events re-run what it does to a freshly rendered form:
 * conditional logic, and the reCAPTCHA widget.
 *
 * reCAPTCHA is the one thing a copy cannot carry. Its widget is an iframe built
 * by Google's script, and GF only draws one into a `.ginput_recaptcha` not yet
 * marked `gform-initialized` — so the copy has that container emptied and
 * unmarked, and the post-render pass draws a fresh widget into it.
 */
function createFormReset( modal ) {
	let pristine = null;
	let formId = '';

	/**
	 * Take the clean copy, once, before the visitor has typed anything.
	 */
	function snapshot() {
		if ( pristine ) {
			return;
		}

		const wrapper = modal.querySelector( '.gform_wrapper[id^="gform_wrapper_"]' );

		if ( ! wrapper ) {
			return;
		}

		formId = wrapper.id.replace( 'gform_wrapper_', '' );
		pristine = wrapper.cloneNode( true );

		pristine.querySelectorAll( '.ginput_recaptcha' ).forEach( ( captcha ) => {
			captcha.classList.remove( 'gform-initialized' );
			captcha.removeAttribute( 'data-widget-id' );
			captcha.removeAttribute( 'data-recaptcha-error' );
			captcha.replaceChildren();
		} );
	}

	/**
	 * Swap the confirmation for a fresh form, if the confirmation is showing.
	 */
	function restore() {
		if ( ! pristine || ! formId ) {
			return;
		}

		const confirmation = modal.querySelector( '#gform_confirmation_wrapper_' + formId );

		if ( ! confirmation ) {
			return;
		}

		confirmation.replaceWith( pristine.cloneNode( true ) );

		// The confirmation arrives with its own #gf_{id} scroll anchor, and the
		// page already had one: drop the extra so GF's scroll-to finds one.
		const anchors = modal.querySelectorAll( '[id="gf_' + formId + '"]' );

		anchors.forEach( ( anchor, i ) => {
			if ( i > 0 ) {
				anchor.remove();
			}
		} );

		// GF clears this itself after a confirmation; set again in case a
		// version ever does not, or the next submit is silently ignored.
		window[ 'gf_submitting_' + formId ] = false;

		// GF's own post-render pass, as after any render of this form. The
		// jQuery event is the fallback for a GF without gform.core.
		const gform = window.gform;

		if ( gform && gform.core && 'function' === typeof gform.core.triggerPostRenderEvents ) {
			gform.core.triggerPostRenderEvents( Number( formId ), 1 );
		} else if ( window.jQuery ) {
			window.jQuery( document ).trigger( 'gform_post_render', [ Number( formId ), 1 ] );
		}
	}

	return { snapshot, restore };
}

function init() {
	const grid = document.querySelector( '.dn-team' );

	if ( ! grid ) {
		return;
	}

	const bioEl = document.getElementById( 'dn-team-bio' );
	const contactEl = document.getElementById( 'dn-team-contact' );
	const bio = bioEl ? createModal( bioEl ) : null;
	const contact = contactEl ? createModal( contactEl ) : null;
	const formReset = contactEl ? createFormReset( contactEl ) : null;

	grid.addEventListener( 'click', ( event ) => {
		const bioButton = event.target.closest( '[data-dn-team-bio]' );

		if ( bioButton && bio ) {
			const card = bioButton.closest( '.dn-team__card' );
			const source = card.querySelector( '.dn-team__bio-source' );
			const body = bio.el.querySelector( '.dn-team__bio-body' );
			const title = bio.el.querySelector( '.dn-modal-title' );

			title.textContent = card.getAttribute( 'data-dn-team-name' ) || '';

			// Replaced, not appended: the dialog is shared, so last time's bio
			// is still in there.
			body.replaceChildren();

			if ( source ) {
				body.appendChild( source.content.cloneNode( true ) );
			}

			bio.open();
			return;
		}

		const mailButton = event.target.closest( '[data-dn-team-contact]' );

		if ( ! mailButton || ! contact ) {
			return;
		}

		const name = mailButton.getAttribute( 'data-dn-team-contact' ) || '';

		// Before the staff field is looked up: if the last message was sent,
		// the field only exists again once the form has been put back.
		if ( formReset ) {
			formReset.snapshot();
			formReset.restore();
		}

		const inputId = contact.el.getAttribute( 'data-dn-team-input' );
		const input = inputId ? document.getElementById( inputId ) : null;

		if ( input ) {
			input.value = name;
		}

		const title = contact.el.querySelector( '.dn-modal-title' );

		if ( title ) {
			// Built here rather than server-side because the dialog is shared:
			// the heading has to name whoever was clicked, and only the click
			// knows that.
			title.textContent = title.getAttribute( 'data-dn-template' )
				? title.getAttribute( 'data-dn-template' ).replace( '%s', name )
				: name;
		}

		contact.open();
	} );
}

if ( 'loading' === document.readyState ) {
	document.addEventListener( 'DOMContentLoaded', init );
} else {
	init();
}
